import {
  factFindToApiAnswers,
  findAnswer,
  findAnswerObj,
  findNumberOfPpItems,
  formatAnswerByOptions,
  formatCoverType,
  formatPpItems,
  getRepeatingGroups,
  includesBuildings,
  includesContents,
  wordsToBools,
  wordsToNumbers,
  wordToPeriod,
} from "@/common/answers";
import { adBoolToString } from "@/common/cover";
import { prepareQuotes } from "@/common/pricing";
import { propertyResponseRequiresEdit } from "@/common/property";
import { isQuilterSpecific } from "@/common/question-set";
import { isObject } from "@/common/transforms";
import {
  INTERFACE_KEY,
  MPPI_COVER_TYPE_OPTIONS,
  MPPI_QUAL_TYPE_OPTIONS,
  PP_ITEM_KEYS,
  SPECIFIED_ITEM_KEYS,
  TERM_OF_INSURANCE_VALUE,
} from "@/constants/answer.constants";
import {
  CALL_CHANNEL,
  PRICED,
  PRODUCT,
  PRODUCT_WHY_PAYMENTSHIELD_POPUP,
  RQS_SYSTEM_ID,
  STEP_TITLE,
} from "@/constants/common.constants";
import {
  CONFIG_CONTENTS_D2C,
  CONFIG_CONTENTS_D2C_MAX,
  CONFIG_CONTENTS_RENTED_ONLY_D2C,
  CONFIG_CONTENTS_RENTED_ONLY_D2C_MAX,
} from "@/constants/contents-config.constants";
import { CONFIG_BUILDINGS_D2C } from "@/constants/excess-config.constants";
import { PROPERTY_DETAILS_INTERFACE_KEY } from "@/constants/form.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import router from "@/router";
import moment from "moment";
import numeral from "numeral";
import {
  ROOF_CONSTRUCTION_ITEMS,
  HOME_ROOF_CONSTRUCTION_ITEMS,
  WALL_CONSTRUCTION_ITEMS,
  HOME_WALL_CONSTRUCTION_ITEMS,
} from "@/constants/component.constants";

export default {
  httpConfig: (state) => {
    if (state.quoteAccessId) {
      return {
        headers: {
          QuoteAccessId: state.quoteAccessId,
          SystemId: RQS_SYSTEM_ID,
        },
      };
    }
    return {
      headers: {
        UserId: state.userId,
        Token: state.token,
        SystemId: state.quoteResponse?.ExternalSystemId || RQS_SYSTEM_ID,
      },
    };
  },
  httpConfigAsRqs: (state) => {
    if (state.quoteAccessId) {
      return {
        headers: {
          QuoteAccessId: state.quoteAccessId,
          SystemId: RQS_SYSTEM_ID,
        },
      };
    }
    return {
      headers: {
        UserId: state.userId,
        Token: state.token,
        SystemId: RQS_SYSTEM_ID,
      },
    };
  },
  getQuoteRequest: (_, getters) => {
    const qr = {
      ProductId: getters.productId,
      BranchNumber: getters.branchNumber,
      IsPartialQuote: true,
      CommissionSacrifice: 0.0,
      CallChannelType: getters.callChannel,
      Answers: getters.preparedAnswers,
    };
    return qr;
  },
  quotes: (state) => state.quotes,
  quoteReference: (state) => state.quoteResponse.QuoteReference,
  getQuoteRequestId: (state) => state.quoteResponse.QuoteRequestId,
  insurerQuotesModel: (state, getters) =>
    prepareQuotes(
      state.quoteResponse,
      getters.coverSelection,
      state.commissionSacrifice
    ),
  activeQuote: (state, getters) => {
    const quotesModel = getters.insurerQuotesModel;
    if (!quotesModel.length || !state.quoteResponse) return;

    return quotesModel.find(
      (quote) =>
        quote.isAvailable &&
        quote.selectedQuote?.QuoteId === state.quoteResponse.ActiveQuoteId
    );
  },
  cheapestQuote: (state, getters) => {
    const insurerQuotesModel = getters.insurerQuotesModel;
    if (!insurerQuotesModel.length || !state.quoteResponse) return null;
    const cheapestInsurer = insurerQuotesModel.find(
      (insurerQuote) => insurerQuote.isCheapest
    );
    return cheapestInsurer?.selectedQuote;
  },
  hasPrices: (state) =>
    state.quoteResponse.Quotes && state.quoteResponse.Prices,
  productId: (state) =>
    parseInt(state.productId) ||
    parseInt(state.quoteResponse?.ProductId) ||
    1015,
  replacementProductId: (state) =>
    parseInt(state.quoteResponse?.ReplacementProductId),
  replacementProductAvailable: (state) =>
    !!state.quoteResponse?.ReplacementProductId,
  hasQuotes: (state) =>
    state.quoteResponse.Quotes && state.quoteResponse.Quotes.length > 0,
  hasCredentials: (state) =>
    state.quoteAccessId !== null ||
    (state.userId !== null && state.token !== null),

  branchNumber: (state) =>
    state.quoteResponse?.BranchNumber || state.branchNumber,
  callChannel: (state) =>
    state.quoteResponse?.CallChannelType || state.callChannel,
  qsId: (state) => state.quoteResponse?.QuestionSetId || state.qsId,
  quoteAccessId: (state) => state.quoteAccessId,
  allIndustries: (state) => state.form.industries,
  allOccupations: (state) => state.form.occupations,
  hasCatalogue: (state) => state.form.occupations > [],
  allQuestions: (state) => {
    // this getter returns an array of all questions
    // we loop through each section then loop again through each Questions array
    // and push each question (object) into the accumulator array
    return state.form.sections.reduce((acc, section) => {
      if (section.Questions) {
        section.Questions.forEach((question) => {
          acc.push(question);
          // for LayoutTableBuilder components we don't need the Value from each input
          // we ONLY want the question Value (array)
          // when the user clicks the 'Add' button their answers get stored in this array
          // this array contains all the values the user entered
          if (
            "Questions" in question &&
            question.AnswerControlType !== "LayoutTableBuilder"
          ) {
            question.Questions.forEach((question) => {
              acc.push(question);
            });
          }
        });
        return acc;
      }
      return acc;
    }, []);
  },
  allSections: (state) => state.form.sections,
  allSectionsEdited: (state) => {
    // returns all sections
    // returns array of objects
    // here Questions array has been edited with the removal of CoverType
    return state.form.sections.map((section) => {
      if (section.Questions) {
        return {
          // use existing section properties
          ...section,
          // loop though each Questions array and remove CoverType
          // assign the returned array to the existing Questions property
          Questions: section.Questions.filter((question) => {
            return question.InterfaceKey !== "CoverType";
          }),
        };
      }
      return {
        ...section,
      };
    });
  },
  getQuestionCoverType: (_, getters) => {
    // Return the CoverType question object, which has { InterfaceKey, Value }
    return getters.allQuestions.find((question) => {
      return question.InterfaceKey === "CoverType";
    });
  },
  getQuestionLayoutPropertyDetails(_, getters) {
    return getters.allQuestions.find((question) => {
      return question.AnswerControlType === "LayoutPropertyDetails";
    });
  },
  getQuoteResultSection: (state) => {
    return state.form.sections.find(
      (x) =>
        x.SectionTitle === STEP_TITLE.yourQuotes ||
        x.SectionTitle === STEP_TITLE.yourQuote
    );
  },
  getQuestionValueByInterfaceKey: (_, getters) => (interfaceKey) => {
    let question = getters.allQuestions.find((question) => {
      return question.InterfaceKey === interfaceKey;
    });

    return question.Value?.Value || question.Value;
  },
  getAllAnswers: (_, getters) => {
    return (
      getters.allQuestions
        .reduce((acc, question) => {
          acc.push(question);
          // check if the question.Value is an array
          // this check is needed for LayoutTableBuilder questions
          if (Array.isArray(question.Value)) {
            // at this point the array contains an object for every input value (4 inputs = 4 objects)
            question.Value.forEach((item) => {
              // inside each object each key has a value (object)
              // we use Object.values() method to store each value inside an array
              // this makes it easier to loop
              const nestedQuestions = Object.values(item);
              nestedQuestions.forEach((item) => {
                // add each object into the current accumulator array
                acc = acc.concat(item);
              });
            });
          }
          return acc;
        }, [])
        // here questions with the DisplayType of 'Layout' are filtered out
        .filter((question) => question.DisplayType !== "Layout")
    );
  },
  getAnswer: (state) => (interfaceKey) => {
    return findAnswer(state.quoteResponse, interfaceKey);
  },
  getNumericAnswer: (state) => (interfaceKey) => {
    return wordsToNumbers(findAnswer(state.quoteResponse, interfaceKey));
  },
  getBoolAnswer: (state) => (interfaceKey) => {
    return wordsToBools(findAnswer(state.quoteResponse, interfaceKey));
  },
  getPeriodAnswer: (state) => (interfaceKey) => {
    return wordToPeriod(findAnswer(state.quoteResponse, interfaceKey));
  },
  getCoverTypeDescription: (state) => {
    return formatCoverType(findAnswer(state.quoteResponse, "CoverType"));
  },
  getPpCount: (state) => {
    return findNumberOfPpItems(state.quoteResponse);
  },
  getPpItems: (state) => {
    return formatPpItems(state.quoteResponse);
  },
  getWorldwideBelongingsItem: (state) => {
    return getRepeatingGroups(state.quoteResponse.Answers, PP_ITEM_KEYS);
  },
  getContentsItem: (state) => {
    return getRepeatingGroups(state.quoteResponse.Answers, SPECIFIED_ITEM_KEYS);
  },
  getCover: (_, getters) => {
    return {
      buildingsCover: {
        amount: getters.getNumericAnswer("BuildingsCoverLevel") || 1000000, //Set default if 0
        excess: getters.getNumericAnswer("ExcessBuildings") || 250,
        noClaim: getters.getNumericAnswer("NoClaimsBuildings"),
      },
      contentsCover: {
        amount: getters.getNumericAnswer("ContentsCoverLevel") || 75000,
        excess: getters.getNumericAnswer("ExcessContents") || 250,
        noClaim: getters.getNumericAnswer("NoClaimsContents"),
      },
      homeEmergency: getters.getBoolAnswer("HomeEmergency"),
      legalExpenses: getters.getBoolAnswer("LegalExpenses"),
      personalPossessions: getters.getPpCount,
      ppItems: getters.getPpItems,
      type: getters.getCoverTypeDescription,
      unspecifiedPpAmount: getters.getAnswer("PersonalPossessionsTotalAmount"),
    };
  },
  getCoverLandlords: (_, getters) => {
    return {
      buildingsCover: {
        amount: 1000000,
        excess: getters.getNumericAnswer("ExcessBuildings"),
        noClaim: getters.getNumericAnswer("NoClaimsBuildings"),
      },
      contentsCover: {
        amount: getters.getNumericAnswer("LandlordContentsLevel"),
        excess: getters.getNumericAnswer("ExcessContents"),
        noClaim: getters.getNumericAnswer("NoClaimsContents"),
      },
      legalExpenses: getters.getBoolAnswer("LegalExpenses"),
      landlordsEmergency: getters.getBoolAnswer("LandlordsEmergency"),
      rentGuarantee: getters.getBoolAnswer("RentGuarantee"),
      type: getters.getCoverTypeDescription,
    };
  },
  getTenantsContentCover: (_, getters) => {
    return {
      contentsCover: {
        amount: getters.getNumericAnswer(INTERFACE_KEY.CONTENTS_COVER_LEVEL),
        excess: getters.getNumericAnswer(INTERFACE_KEY.EXCESS_CONTENTS),
        noClaim: getters.getNumericAnswer(INTERFACE_KEY.NO_CLAIMS_CONTENTS),
      },
      legalExpenses: getters.getBoolAnswer(INTERFACE_KEY.LEGAL_EXPENSES),
      accidentalDamageContents: getters.getBoolAnswer(
        "AccidentalDamageContents"
      ),
      valuables: getters.getContentsItem,
      ppItems: getters.getPpItems,
      worldwideBelongings: getters.getWorldwideBelongingsItem,
      unspecifiedPpAmount: getters.getAnswer("PersonalPossessionsTotalAmount"),
      type: "Contents only",
    };
  },
  getTenantsLiabilityCover: () => {
    return {
      type: "Contents only",
    };
  },
  getMPPICover: (_, getters) => {
    return {
      benefitPeriod: getters.getPeriodAnswer(INTERFACE_KEY.BENEFIT_PERIOD),
      type: formatAnswerByOptions(
        getters.getAnswer(INTERFACE_KEY.COVER_TYPE),
        MPPI_COVER_TYPE_OPTIONS
      ),
      disabilityQualificationPeriod: getters.getPeriodAnswer(
        INTERFACE_KEY.DISABILITY_QUALIFICATION_PERIOD
      ),
      disabilityQualificationType: formatAnswerByOptions(
        getters.getAnswer(INTERFACE_KEY.DISABILITY_QUALIFICATION_TYPE),
        MPPI_QUAL_TYPE_OPTIONS
      ),
      unemploymentQualificationPeriod: getters.getPeriodAnswer(
        INTERFACE_KEY.UNEMPLOYMENT_QUALIFICATION_PERIOD
      ),
      unemploymentQualificationType: formatAnswerByOptions(
        getters.getAnswer(INTERFACE_KEY.UNEMPLOYMENT_QUALIFICATION_TYPE),
        MPPI_QUAL_TYPE_OPTIONS
      ),
    };
  },
  hasPropertyDetail: (_, getters) => {
    // Has everything from peoperty been answered
    return (
      getters.getAnswer("InsuredAddressPostCode") &&
      getters.getAnswer("InsuredAddressStreet") &&
      getters.getAnswer("InsuredAddressTown") &&
      getters.getAnswer("InsuredAddressCounty") &&
      getters.getAnswer("PropertyType") &&
      getters.getAnswer("BuildYear") &&
      getters.getAnswer("NumberOfBedrooms") &&
      getters.getAnswer("NumberOfBathrooms") &&
      getters.getAnswer("WallConstruction") &&
      getters.getAnswer("RoofConstruction") &&
      getters.getAnswer("OwnershipProperty") &&
      getters.getAnswer("NumberOfAdults") &&
      getters.getAnswer("NumberOfChildren") &&
      getters.getAnswer("NoClaimsBuildings") &&
      getters.getAnswer("NoClaimsContents")
    );
  },
  preparedAnswers: (state, getters) => {
    // Prepare answers to send to PSL API
    let preparedAnswers = getters.getAllAnswers
      .map((question) => {
        // question.Value refers to the actual value the user entered/selected
        // the Value property inside each input component returns a string EXCEPT select components (see SelectBase.vue)
        // here we check if the question.Value property is an object (Type is a select component, Value will be an object)
        if (question.Value && isObject(question.Value)) {
          return {
            // the extra .Value refers to the Value property returned from return-object (see SelectBase.vue)
            Value: question.Value.Value,
            InterfaceKey: question.InterfaceKey,
            RepeatingQuestionSetIndex: question.RepeatingQuestionSetIndex,
          };
        }

        if (
          question.Value === null ||
          question.Value === undefined ||
          question.Value.toString() === ""
        )
          return null;

        let value = question.Value.toString();
        if (question.InterfaceKey == "PreviousClaimAmountOfLoss") {
          value = numeral(value).value().toString();
        }

        return {
          Value: value,
          InterfaceKey: question.InterfaceKey,
          RepeatingQuestionSetIndex: question.RepeatingQuestionSetIndex,
        };
      })
      .filter((x) => x !== null);

    const observer = state.currentRefObserverForm;

    if (observer && !observer.flags.valid) {
      preparedAnswers = preparedAnswers.filter(
        (a) =>
          !(
            a.InterfaceKey in observer.fields &&
            observer.fields[a.InterfaceKey].invalid
          )
      );
    }

    const coverTypeQuestion = getters.getQuestionCoverType;

    const factFindAnswers = isQuilterSpecific(getters.qsId)
      ? factFindToApiAnswers(state.factFind)
      : [];

    //format date of birth
    let dateOfBirthFields = preparedAnswers.filter((answer) =>
      answer.InterfaceKey.includes("DoB")
    );
    dateOfBirthFields.forEach((field) => {
      if (field && field.Value) {
        field.Value = moment(field.Value, "DD/MM/YYYY hh:mm:ss")
          .format("DD/MM/YYYY")
          .toString();
      }
    });

    let allAnswers = [
      ...getters.getHomeEligibility,
      ...factFindAnswers,
      ...preparedAnswers,
      ...state.coverAnswers,
    ];

    // Remove Buildings or Contents coverAnswers keys if not applicable
    if (coverTypeQuestion && !includesBuildings(coverTypeQuestion)) {
      allAnswers = allAnswers.filter(
        (answer) =>
          answer.InterfaceKey !== "BuildingsCoverLevel" &&
          answer.InterfaceKey !== "ExcessBuildings" &&
          answer.InterfaceKey !== "NoClaimsBuildings"
      );
    }
    if (coverTypeQuestion && !includesContents(coverTypeQuestion)) {
      allAnswers = allAnswers.filter(
        (answer) =>
          answer.InterfaceKey !== "ContentsCoverLevel" &&
          answer.InterfaceKey !== "ExcessContents" &&
          answer.InterfaceKey !== "NoClaimsContents" &&
          answer.InterfaceKey !== "PPItemDescription" &&
          answer.InterfaceKey !== "SpecifiedItemDescription" &&
          answer.InterfaceKey !== "PPItemType" &&
          answer.InterfaceKey !== "SpecifiedItemType" &&
          answer.InterfaceKey !== "PPItemValue" &&
          answer.InterfaceKey !== "PersonalPossessionsToSpecify" &&
          answer.InterfaceKey !== "ContentsItemsToSpecify" &&
          answer.InterfaceKey !== "SpecifiedItemValue" &&
          answer.InterfaceKey !== "PersonalPossessionsTotalAmount" &&
          answer.InterfaceKey !== "AccidentalDamageContents" &&
          answer.InterfaceKey !== "LandlordContentsLevel"
      );
    }

    // TODO: Remove after the MPPI question set removed QualTypeUnemployment and QualPeriodUnemployment
    if (
      getters.isMPPI &&
      coverTypeQuestion &&
      !(
        coverTypeQuestion.Value === "UnemploymentAndDisability" ||
        coverTypeQuestion.Value === "Unemployment"
      )
    ) {
      allAnswers = allAnswers.filter(
        (answer) =>
          answer.InterfaceKey !== "QualTypeUnemployment" &&
          answer.InterfaceKey !== "QualPeriodUnemployment"
      );
    }

    const index = allAnswers.findIndex(
      (x) => x.InterfaceKey === "SetDefaultUnapprovedMortgageApplication"
    );
    if (index !== -1) {
      allAnswers.splice(index, 1);
      const ownershipRequiresUnapprovedMortgage = [
        "OwnedMortgaged",
        "SharedOwnership",
      ];
      const unapprovedMortgageField = allAnswers.find(
        (answers) => answers.InterfaceKey === "UnapprovedMortgageApplication"
      );
      const ownershipPropertyField = allAnswers.find(
        (answers) => answers.InterfaceKey === "OwnershipProperty"
      );
      const UnapprovedMortgageIsRequired =
        ownershipRequiresUnapprovedMortgage.includes(
          ownershipPropertyField?.Value
        );
      if (!unapprovedMortgageField && UnapprovedMortgageIsRequired) {
        allAnswers.push({
          Value: false,
          InterfaceKey: "UnapprovedMortgageApplication",
        });
      }
    }

    const coverSelectionObject = getters.coverSelection;
    if (coverSelectionObject) {
      if (coverSelectionObject.buildingsAd) {
        allAnswers.push({
          Value: coverSelectionObject.buildingsAd,
          InterfaceKey: "AccidentalDamageBuildings",
        });
      }
      if (coverSelectionObject.contentsAd) {
        allAnswers.push({
          Value: coverSelectionObject.contentsAd,
          InterfaceKey: "AccidentalDamageContents",
        });
      }
    }

    return allAnswers;
  },
  preparedAddress: (_, getters) => {
    const addressFields = [
      "InsuredAddressStreet",
      "InsuredAddressDistrict",
      "InsuredAddressTown",
      "InsuredAddressCounty",
      "InsuredAddressPostCode",
    ];
    let addressData = {};
    getters.getAllAnswers
      .filter((question) => question.Value && !isObject(question.Value))
      .forEach((question) => {
        if (addressFields.indexOf(question.InterfaceKey) !== -1) {
          addressData[question.InterfaceKey] = question.Value.toString();
        }
      });
    return addressData;
  },
  propertyDataRequiresEdit: (state) => {
    return propertyResponseRequiresEdit(state.lexisNexisResponse);
  },
  propertyAnswersAsPropertyDetails: (_, getters) => {
    const propertyLayout = getters.getQuestionLayoutPropertyDetails;
    let propertyDetailsModel = {};
    propertyLayout?.Questions?.forEach((question) => {
      if (isObject(question.Value) && question.Value.Value) {
        propertyDetailsModel[question.InterfaceKey] = question.Value.Value;
      } else {
        propertyDetailsModel[question.InterfaceKey] = question.Value;
      }
    });
    return propertyDetailsModel;
  },

  propertyDetailsAnswer: (_, getters) => {
    let propertyDetailsModel = {};
    const listPropertyDetailInterfaceKeyQuestions =
      PROPERTY_DETAILS_INTERFACE_KEY;
    const propertyDetailQuestions = getters.allQuestions.filter((item) => {
      return listPropertyDetailInterfaceKeyQuestions.includes(
        item.InterfaceKey
      );
    });
    propertyDetailQuestions.forEach((question) => {
      propertyDetailsModel[question.InterfaceKey] = question.Value;
    });
    return propertyDetailsModel;
  },
  repeatingQuestionSetIndex: (state) => {
    return state.form.repeatingQuestionSetIndex;
  },
  showRentalError: (state, getters) => (coverType) => {
    const coverTypeQuestion = coverType || getters.getQuestionCoverType;
    const ownershipQuestion = state.questionMap.OwnershipProperty;
    if (!coverTypeQuestion || !ownershipQuestion) return false;

    return (
      includesBuildings(coverTypeQuestion) &&
      ownershipQuestion.Value.includes("Rented")
    );
  },
  isDrawerCustomiseCoverPopulatedFromApi: (state) => {
    return state.isDrawerCustomiseCoverPopulatedFromApi;
  },
  coverSelection: (state, getters) => {
    const coverSelectionObject = {
      buildingsAd: state.buildingsAd,
      contentsAd: state.contentsAd,
    };
    // Remove irrelevant bits (if buildings/contents only)
    const coverTypeQuestion = getters.getQuestionCoverType;
    if (!coverTypeQuestion) return coverSelectionObject;

    if (getters.isContentsOnlyProduct) {
      return coverSelectionObject;
    }

    if (!includesBuildings(coverTypeQuestion)) {
      coverSelectionObject.buildingsAd = null;
    } else if (!includesContents(coverTypeQuestion)) {
      coverSelectionObject.contentsAd = null;
    }
    return coverSelectionObject;
  },
  ncdBuildingsState: (state) => {
    return state.ncdBuildingsState;
  },
  isAdvisedSeller: (_, getters) => {
    const callChannel = getters.callChannel;
    return (
      callChannel === CALL_CHANNEL.advised ||
      callChannel === CALL_CHANNEL.referralTelephony
    );
  },
  isSelfServe: (_, getters) => {
    return getters.callChannel === CALL_CHANNEL.referralSelfServe;
  },
  isSelfServeJourney: (_, getters) => {
    return (
      getters.isSelfServe ||
      router.currentRoute.path.includes("d2c") ||
      // TODO: just for home landing page only, will be refactored later
      router.currentRoute.name === ROUTE_NAME.HOME.D2C.LANDING_PAGE
    );
  },
  isQuilterFactFind: (_, getters) => {
    return (
      getters.callChannel === CALL_CHANNEL.intrinsic &&
      isQuilterSpecific(getters.qsId)
    );
  },
  quilterSelectedCoverMatchesFactFind: (state, getters) => {
    const buildingsCoverRequired = state.factFind.buildingsCover;
    const buildingsAccidentalCoverRequired =
      state.factFind.buildingsAccidentalCover;
    const contentsCoverRequired = state.factFind.contentsCover;
    const contentsAccidentalCoverRequired =
      state.factFind.contentsAccidentalCover;
    const coverPersonalBelongingsRequired =
      state.factFind.coverPersonalBelongings;
    const coverHomeEmergenciesRequired = state.factFind.coverHomeEmergencies;
    const coverLegalExpensesRequired = state.factFind.coverLegalExpenses;

    const buildingsCoverMatchesRequirement =
      buildingsCoverRequired ===
      includesBuildings(getters.getQuestionCoverType);
    const buildingsAccidentalCoverMatchesRequirement =
      buildingsAccidentalCoverRequired === !!getters.coverSelection.buildingsAd;
    const contentsCoverMatchesRequirement =
      contentsCoverRequired === includesContents(getters.getQuestionCoverType);
    const contentsAccidentalCoverMatchesRequirement =
      contentsAccidentalCoverRequired === !!getters.coverSelection.contentsAd;
    const coverPersonalBelongingsMatchesRequirement =
      coverPersonalBelongingsRequired === !!getters.getPpCount;
    const coverHomeEmergenciesMatchesRequirement =
      coverHomeEmergenciesRequired === getters.getBoolAnswer("HomeEmergency");
    const coverLegalExpensesMatchesRequirement =
      coverLegalExpensesRequired === getters.getBoolAnswer("LegalExpenses");

    return (
      buildingsCoverMatchesRequirement &&
      buildingsAccidentalCoverMatchesRequirement &&
      contentsCoverMatchesRequirement &&
      contentsAccidentalCoverMatchesRequirement &&
      coverPersonalBelongingsMatchesRequirement &&
      coverHomeEmergenciesMatchesRequirement &&
      coverLegalExpensesMatchesRequirement
    );
  },
  landlordsQuilterSelectedCoverMatchesFactFind: (state, getters) => {
    const buildingsAccidentalCoverRequired =
      state.factFind.buildingsAccidentalCover;
    const contentsCoverRequired = state.factFind.contentsCover;
    const contentsAccidentalCoverRequired =
      state.factFind.contentsAccidentalCover;
    const coverLandlordsEmergenciesRequired =
      state.factFind.landlordCoverEmergencies;
    const coverLegalExpensesRequired =
      state.factFind.landlordCoverLegalExpenses;
    const coverRentProtection = state.factFind.landlordCoverRentProtection;

    const buildingsAccidentalCoverMatchesRequirement =
      buildingsAccidentalCoverRequired === !!getters.coverSelection.buildingsAd;
    const contentsCoverMatchesRequirement =
      contentsCoverRequired === includesContents(getters.getQuestionCoverType);
    const contentsAccidentalCoverMatchesRequirement =
      contentsAccidentalCoverRequired === !!getters.coverSelection.contentsAd;
    const coverLandlordsEmergenciesMatchesRequirement =
      coverLandlordsEmergenciesRequired ===
      getters.getBoolAnswer("LandlordsEmergency");
    const coverLegalExpensesMatchesRequirement =
      coverLegalExpensesRequired === getters.getBoolAnswer("LegalExpenses");
    const coverRentProtectionRequirement =
      coverRentProtection === getters.getBoolAnswer("RentGuarantee");

    return (
      buildingsAccidentalCoverMatchesRequirement &&
      contentsCoverMatchesRequirement &&
      contentsAccidentalCoverMatchesRequirement &&
      coverLandlordsEmergenciesMatchesRequirement &&
      coverLegalExpensesMatchesRequirement &&
      coverRentProtectionRequirement
    );
  },
  specifiedValuables(_, getters) {
    const isValue = (item) =>
      item.InterfaceKey === "SpecifiedItemValue" ||
      item.InterfaceKey === "PPItemValue";
    const filteredPpItems = getters.getCover.ppItems.filter(
      (item) => item !== undefined
    );
    const count = filteredPpItems?.length;
    if (count) {
      const ppItemsValues = getters.getCover.ppItems.map((item) =>
        item.find(isValue)
      );
      return {
        count,
        sum: ppItemsValues.reduce(
          (acc, current) => acc + Number(current.Value),
          0
        ),
      };
    } else {
      return {
        count: 0,
        sum: 0,
      };
    }
  },
  isExpired: (state) => {
    return state.isExpired;
  },
  contactInfo: (state) => state.contactInfo,
  getContentConfigD2C: (_, getters) => {
    const ownershipProperty = getters.getAnswer("OwnershipProperty");
    const needMaxContentConfig =
      getters.getNumericAnswer("NumberOfBedrooms") > 6 ||
      getters.getNumericAnswer("NumberOfBathrooms") > 6;

    if (
      ownershipProperty === "RentedFromLocalAuthorityOrHousingAssociation" ||
      ownershipProperty === "RentedFromPrivateLandlord"
    ) {
      return needMaxContentConfig
        ? CONFIG_CONTENTS_RENTED_ONLY_D2C_MAX
        : CONFIG_CONTENTS_RENTED_ONLY_D2C;
    } else {
      return needMaxContentConfig
        ? CONFIG_CONTENTS_D2C_MAX
        : CONFIG_CONTENTS_D2C;
    }
  },

  getBuildingConfigD2C: () => {
    return CONFIG_BUILDINGS_D2C;
  },
  isNumberOfBedroomsOrBathRoomGreaterThan6: (_, getters) => {
    return (
      getters.getNumericAnswer("NumberOfBedrooms") > 6 ||
      getters.getNumericAnswer("NumberOfBathrooms") > 6
    );
  },
  isOwnershipPropertyRented: (_, getters) => {
    return (
      getters.getAnswer("OwnershipProperty") ===
        "RentedFromLocalAuthorityOrHousingAssociation" ||
      getters.getAnswer("OwnershipProperty") === "RentedFromPrivateLandlord"
    );
  },
  needRefreshQuote: (state) => state.needRefreshQuote,
  getMaxCommissionSacrifice: (state) => {
    return (
      state.quoteResponse?.MaxCommissionSacrifice ??
      state.maxCommissionSacrifice
    );
  },
  isProductTransferInProgress: (state) => state.productTransferInProgress,
  getHomeEligibility: (state, getters) => {
    if (state.quoteResponse?.ProductId == PRODUCT.LandLord) {
      return [];
    }
    if (!getters.isSelfServe) {
      return [
        {
          InterfaceKey: "HomeEligibility",
          Value: "True",
        },
      ];
    }
    return state.homeEligibility;
  },
  getAnswerObject: (state) => (interfaceKey) => {
    return findAnswerObj(state.quoteResponse, interfaceKey);
  },
  getIsAnnualRequote: (state) => state.isAnnualRequote,
  getQuoteRequestIdAfterRetrieve: (state) => state.quoteRequestIdAfterRetrieve,
  canSelectForComparison: (state) => {
    return state.capabilities?.CanSelectForComparison;
  },
  isQuotePriced: (state) => state.quoteStatus === PRICED,
  clientErrorMessagesArray: (state) => {
    return (
      state.lastClientErrorResponse?.Messages[0]?.Items ??
      new Array(state.lastClientErrorResponse?.Messages[0]?.Summary)
    );
  },
  getQuoteExpiryDate: (state) => state.quoteResponse.QuoteExpiryDate,
  getQuoteExcessConfiguration: (state) => state.quoteExcessConfiguration,
  isLandlords: (state) =>
    state.productId === PRODUCT.LandLord ||
    window.location.href.indexOf("landlords/") !== -1,
  isTenantsContents: (state) =>
    state.productId === PRODUCT.Tenants_Contents ||
    window.location.href.indexOf("tenants-contents/") !== -1,
  isHome: (state) =>
    state.productId === PRODUCT.Home ||
    state.productId === PRODUCT.HomeOld ||
    window.location.href.indexOf("home/") !== -1,
  isMultiTier: (state) => state.quoteResponse.IsMultiTier,
  isContentsOnlyProduct(_, getters) {
    return [PRODUCT.Tenants_Contents].includes(getters.productId);
  },
  termOfInsurance: (_, getters) => {
    const answer = getters.getAnswer(INTERFACE_KEY.TERM_OF_INSURANCE);

    return TERM_OF_INSURANCE_VALUE[answer];
  },
  isQuestionSteps: (state) => state.currentPath?.includes("/quotation"),
  isQuoteResultPage: (state) => state.currentPath?.includes("/your-quotes"),
  selectedAds: (_, getters) => {
    return {
      buildingsAd: adBoolToString(getters.coverSelection.buildingsAd),
      contentsAd: adBoolToString(getters.coverSelection.contentsAd),
    };
  },
  roofConstructionItems: (_, getters) => {
    switch (getters.productId) {
      case PRODUCT.Home:
        return HOME_ROOF_CONSTRUCTION_ITEMS;
      default:
        return ROOF_CONSTRUCTION_ITEMS;
    }
  },
  wallConstructionItems: (_, getters) => {
    switch (getters.productId) {
      case PRODUCT.Home:
        return HOME_WALL_CONSTRUCTION_ITEMS;
      default:
        return WALL_CONSTRUCTION_ITEMS;
    }
  },
  isMPPI: (_, getters) => {
    return getters.productId === PRODUCT.MPPI;
  },
  isShowPremiumFlex: (_, getters) => {
    return (
      !getters.isAdvisedSeller && Number(getters.getMaxCommissionSacrifice) > 0
    );
  },
  getWhyChooseUsComponent: (_, getters) => {
    return PRODUCT_WHY_PAYMENTSHIELD_POPUP[getters.productId];
  },
  getRentCoverConfirmation: (state) => state.rentCoverConfirmation,
};
